/**
 * FontFamilyControl — Font family selector (system, serif, monospace).
 *
 * @module components/accessibility/FontFamilyControl
 */
import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import {
  useAccessibilitySettings,
  type AccessibilityFontFamily,
} from '../../hooks/useAccessibilitySettings';

const FONT_FAMILY_OPTIONS: Array<{
  value: AccessibilityFontFamily;
  label: ReturnType<typeof msg>;
  css: string;
}> = [
  { value: 'system', label: msg`System Default`, css: 'system-ui, -apple-system, sans-serif' },
  { value: 'serif', label: msg`Serif`, css: 'Georgia, "Times New Roman", serif' },
  { value: 'monospace', label: msg`Monospace`, css: '"Fira Code", "Consolas", monospace' },
];

export const FontFamilyControl: React.FC = () => {
  const { t } = useLingui();
  const { fontFamily, updateSettings } = useAccessibilitySettings();

  const handleChange = (e: SelectChangeEvent) => {
    updateSettings({ fontFamily: e.target.value as AccessibilityFontFamily });
  };

  return (
    <Box>
      <FormControl fullWidth size="small">
        <InputLabel id="a11y-font-family-label">
          <Trans>Font Family</Trans>
        </InputLabel>
        <Select
          labelId="a11y-font-family-label"
          value={fontFamily}
          onChange={handleChange}
          label={t`Font Family`}
          data-testid="a11y-font-family-select"
        >
          {FONT_FAMILY_OPTIONS.map((opt) => (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{ fontFamily: opt.css }}
            >
              {t(opt.label)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default FontFamilyControl;
